import React, { useState, useEffect, useRef, useContext } from 'react';
import VoiceContext from './VoiceContext';
import VoiceDialog from '../../shared-components/VoiceDialog';
import levenshteinDistance from '../../utils/levenshteinDistance';
import { GlobalContext } from '../../GlobalContext';


const SpeechRecognitionConstructor =
    (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

if (!SpeechRecognitionConstructor) {
    console.error('SpeechRecognition API is not supported in this browser.');
}

type Props = {
    children?: React.ReactNode;
};

export default function VoiceProvider({ children }: Props) {
    const [isListeningEnabled, setIsListeningEnabled] = useState(() => localStorage.getItem('savedVoiceOption') === 'yes')
    const [isListening, setIsListening] = useState(false); // Tracks user intent for voice mode
    const [microphonePermission, setMicrophonePermission] = useState<string | null>(null)
    const [transcript, setTranscript] = useState('');
    const [finalTranscript, setFinalTranscript] = useState('');
    const [spokenAnswer, setSpokenAnswer] = useState('');
    const [finalSpokenAnswer, setFinalSpokenAnswer] = useState('')
    const [recognitionStatus, setRecognitionStatus] = useState('');
    const recognitionRef = useRef<any>(null); // Use 'any' for compatibility
    //const synthesisRef = useRef<SpeechSynthesis | null>(null);
    const [registeredCommands, setRegisteredCommands] = useState<string[]>([]);
    const [voiceCommand, setVoiceCommand] = useState<string>('');
    //const [pausedForSpeakingCount, setPausedForSpeakingCount] = useState<number>(0);
    const registeredCommandsRef = useRef<string[]>([]);
    const questionTypesIncompatibleWithVoiceMode = ['Signature', 'Location', 'MultipleFields', 'BarCode']
    const { setShowModal, t } = useContext(GlobalContext)
    const recogniserIsRunningRef = useRef(false);


    const isListeningEnabledRef = useRef(false)
    useEffect(() => { isListeningEnabledRef.current = isListeningEnabled; }, [isListeningEnabled])

    const pausedCountRef = useRef(0);

    useEffect(() => {
        // console.log("👄 Registered commands updated:", registeredCommands);
        registeredCommandsRef.current = registeredCommands;
    }, [registeredCommands]);


    useEffect(() => {
        if (isListeningEnabled) {
            checkPermission()
        }
        let permissionStatus: any
        async function checkPermission() {
            try {
                permissionStatus = await navigator.permissions.query({
                    name: "microphone" as PermissionName,
                })
                console.log("🎤 Microphone permission status:", permissionStatus.state);
                setMicrophonePermission(permissionStatus.state)

                // Listen for changes
                permissionStatus.onchange = () => {
                    setMicrophonePermission(permissionStatus.state)
                }
            } catch (err) {
                console.error("Permissions API not supported", err)
            }
        }
        return () => {
            if (permissionStatus) {
                permissionStatus.onchange = null;
            }
        };
    }, [isListeningEnabled])



    useEffect(() => {
        if (transcript) {
            setSpokenAnswer(transcript)
        }
    }, [transcript])

    // log out recognition status changes
    // useEffect(() => {
    //     console.log("🎤 Recognition status:", recognitionStatus);
    // }, [recognitionStatus])

    useEffect(() => {
        if (finalTranscript) {
            setFinalSpokenAnswer(finalTranscript)
        }
    }, [finalTranscript])



    useEffect(() => {
        if (recognitionRef.current) {
            recognitionRef.current.onaudiostart = () => setRecognitionStatus('🎧 Processing audio...');
            recognitionRef.current.onspeechstart = () => {
                setRecognitionStatus('🗣️ Speech detected!')
                setShowModal({ heardVoiceAnswer: t('UIElements.MessageModal.processing') })
            }
            recognitionRef.current.onspeechend = () => setRecognitionStatus('🤐 Speech ended.');

        }
    }, []);

    // this is to handle when the user switches tabs or minimizes the browser then comes back
    // useEffect(() => {
    //     const onVis = () => {
    //         if (document.visibilityState === "visible") {
    //             restartIfNeeded("visibilitychange");
    //         }
    //     };
    //     document.addEventListener("visibilitychange", onVis);
    //     window.addEventListener("focus", onVis);
    //     return () => {
    //         document.removeEventListener("visibilitychange", onVis);
    //         window.removeEventListener("focus", onVis);
    //     };
    // }, []);


    const restartIfNeeded = (reason: string) => {
        // Only restart if user intends voice mode and we're not paused for TTS
        console.log(`Checking if we should restart recognition after ${reason}...`);
        if (pausedCountRef.current > 0) {
            console.log(`Not restarting because pausedCountRef.current > 0 (it is ${pausedCountRef.current})`);
            return;
        }
        if (document.visibilityState !== "visible") {
            console.log(`Not restarting because document.visibilityState !== 'visible' (it is ${document.visibilityState})`);
            return;
        }
        if (!recognitionRef.current) {
            console.log("Not restarting because !recognitionRef.current");
            return;
        }

        // Chrome can throw InvalidStateError if you call start too fast after onend
        setTimeout(() => {
            try {
                if (reason == 'onerror:aborted') {
                    console.log("Creating a new one")
                    // clear handlers, we're going to make a new one next so we can get rid of these
                    recognitionRef.current.onend = null; // Prevent onend from firing after abort
                    recognitionRef.current.onerror = null; // Prevent onerror from firing after abort
                    recognitionRef.current.onaudiostart = null;
                    recognitionRef.current.onspeechstart = null;
                    recognitionRef.current.onspeechend = null;
                    recognitionRef.current = null
                    setUpRecognitionHandlers();
                    recognitionRef.current.start();
                    setRecognitionStatus(`🔁 Restarted after abort`);

                } else {
                    console.log(`Restarting recognition due to ${reason} - this is normal if you switched tabs or minimized the browser and came back`);
                    recognitionRef.current.onend = null; // Prevent onend from firing after abort
                    recognitionRef.current.onerror = null; // Prevent onerror from firing after abort
                    recognitionRef.current.onaudiostart = null;
                    recognitionRef.current.onspeechstart = null;
                    recognitionRef.current.onspeechend = null;
                    recognitionRef.current = null
                    setTimeout(() => {
                        setUpRecognitionHandlers();
                        recognitionRef.current.start();                    
                        setRecognitionStatus(`🔁 Restarted (${reason})`);
                    }, 1000) // Give it a moment before restarting to avoid rapid start/stop loops
                }
            } catch (e) {
                // ignore InvalidStateError; it means it's already starting/started
                if (!(e instanceof DOMException && e.name === "InvalidStateError")) {
                    console.warn("Failed to restart recognition:", e);
                } else {
                    console.log("Attempted to restart recognition but it was already running (InvalidStateError), which can happen if onend and onerror fire in quick succession. This is usually not a problem.");
                }
            }
        }, 250);
    };



    const startListening = (emoji = '👂') => {
        console.log(`💬 ${emoji} Starting speech recognition`);
        if (!recognitionRef.current) {
            setUpRecognitionHandlers();
        }
        try {
            recognitionRef.current.start();
        } catch (e) {
            // ignore InvalidStateError; it means it's already started
            if (!(e instanceof DOMException && e.name === "InvalidStateError")) {
                console.warn("Failed to start recognition:", e);
            }
        }
    }

    const clearHandlersFromSpeechRecognition = () => {
        if (!recognitionRef.current) return;
        recognitionRef.current.onstart = null;
        recognitionRef.current.onend = null;
        recognitionRef.current.onerror = null;
        recognitionRef.current.onresult = null;
        recognitionRef.current.onaudiostart = null;
        recognitionRef.current.onspeechstart = null;
        recognitionRef.current.onspeechend = null;
    }

    const stopListening = (emoji = '⛔️') => {
        console.log(`💬 ${emoji} Stopping speech recognition`);
        // get rid of onend etc so it doesn't restart itself
        clearHandlersFromSpeechRecognition();
        recognitionRef.current && recognitionRef.current.abort();
        recognitionRef.current = null; // Clear the instance so we create a new one with fresh handlers next time

    };


    const registerCommands = (commands: string[]) => {
        // console.log('💬📣 Registering voice commands:', commands);
        setRegisteredCommands(commands);
    };


    const clearVoiceCommand = () => {
        setVoiceCommand('')
    }

    const clearSpokenAnswer = () => {
        console.log("💬 Clearing spoken answer")
        setSpokenAnswer('')
        setFinalSpokenAnswer('')
    };

    const setUpRecognitionHandlers = () => {
        console.log("Setting up speech recognition handlers");
        if (recognitionRef.current) {
            console.log("Speech recognition instance already exists, skipping setup");
            return;
        }
        const browser_language_code = navigator.language || 'en-GB';
        console.log('Setting up speech recognition with language:', browser_language_code);

        recognitionRef.current = SpeechRecognitionConstructor
            ? new SpeechRecognitionConstructor()
            : null;
        if (!recognitionRef.current) return;
        console.log("💣 Speech recognition instance created");
        recognitionRef.current.continuous = true;
        recognitionRef.current.interimResults = true;
        recognitionRef.current.lang = browser_language_code;
        recognitionRef.current.onstart = () => {
            setRecognitionStatus("🎙️ Listening...");
            recogniserIsRunningRef.current = true;
            setMicrophonePermission('granted');
        };

        recognitionRef.current.onend = () => {
            setRecognitionStatus("🛑 Recognition ended");
            recogniserIsRunningRef.current = false;
            restartIfNeeded("onend");
        };

        recognitionRef.current.onerror = (e: any) => {
            setRecognitionStatus(`⚠️ Error: ${e?.error || "unknown"}`);
            recogniserIsRunningRef.current = false;

            // Some errors mean "don't auto-restart" (permission etc.)
            const fatal = ["not-allowed", "service-not-allowed", "audio-capture"];
            if (e?.error === "not-allowed") {
                setMicrophonePermission('denied');
            }
            if (!fatal.includes(e?.error)) {
                console.log(`💬⚠️ Non-fatal error occurred (${e?.error}:${e?.message || "unknown"}), will attempt to restart recognition.`);
                restartIfNeeded(`onerror:${e?.error || "unknown"}`);
            }
        };

        recognitionRef.current.onresult = (event: any) => {
            // console.log("Speech recognition result event:", event);
            const lastResultIndex = event.results.length - 1; // Get the most recent result
            const lastResult = event.results[lastResultIndex];
            const alternatives = Array.from(lastResult).map((alt: any) => ({
                transcript: alt.transcript,
                confidence: alt.confidence,
            }));

            if (lastResult.isFinal) {
                setRecognitionStatus("🎙️ Processing final results...");
                const rawTranscript = lastResult[0].transcript
                const finalTranscriptClean = rawTranscript.split('.').join(' ').split(',').join(' ').split('!').join(' ').split('?').join(' ');
                const finalTranscriptTextTrimmed = finalTranscriptClean.trim();
                const finalTranscriptLower = finalTranscriptTextTrimmed.toLowerCase();
                // remove any punctuation from the final transcript for better command matching by splitting and joining full stops etc
                // (Edge adds full stops)
                for (const command of registeredCommandsRef.current) {
                    // loop through words in finalTranscript and see if any match the command, allowing for some minor misrecognition using Levenshtein distance
                    for (const word of finalTranscriptLower.split(" ")) {
                        const distance = levenshteinDistance(word, command);
                        // const threshold = 1
                        const threshold = command.length <= 4 ? 0 : 1; // Allow more distance for longer commands
                        if (distance <= threshold) {
                            if (distance === 0) {
                                console.log(`💬📣✅ Command recognized: ${command}`);
                            } else {
                                console.log(`💬📣✅ Command recognized with minor misrecognition: ${command} (matched word: ${word}, distance: ${distance})`);
                            }
                            setVoiceCommand(command);
                            return; // Exit loop on first match
                        }
                    }
                }

                console.log(`💬📝✅ ${finalTranscriptTextTrimmed}`)
                setTranscript(finalTranscriptTextTrimmed); // Only set the final transcript
                setFinalTranscript(finalTranscriptTextTrimmed)
            } else {
                setRecognitionStatus("🎙️ Processing interim results...");
                // console.log("Processing interim results...");
                // iterate through the alternatives and see if they match an expected command
                for (const alt of alternatives) {
                    const altTranscript = alt.transcript.trim().toLowerCase();
                    // console.log("Checking alternative transcript to see if it is a command:", altTranscript);
                    if (registeredCommandsRef.current.includes(altTranscript)) {
                        // console.log(`💬👂📣 Command recognized: ${altTranscript}`);
                        // setVoiceCommand(altTranscript);
                        // return; // Exit  on first match
                    }
                }
                const interimTranscript = lastResult[0].transcript; // Only use the latest interim result


                console.log(`💬📝👂 ${interimTranscript}`)
                setTranscript(interimTranscript);

            }

            // Handle multiple alternatives


        };
    };

    const isRecognizerRunning = !!recognitionRef.current && recogniserIsRunningRef.current;



    // log out paused for speaking count

    const speechRecognitionSupported = () => {
        const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition || null

        return SpeechRecognition !== null
    }



    return (
        <VoiceContext.Provider
            value={{
                isListening,
                transcript,
                spokenAnswer,
                finalSpokenAnswer,
                recognitionStatus,
                startListening,
                stopListening,
                registerCommands,
                isRecognizerRunning,
                voiceCommand,
                clearVoiceCommand,
                clearSpokenAnswer,
                speechRecognitionSupported,
                questionTypesIncompatibleWithVoiceMode,
                microphonePermission,
                registeredCommands,
                isListeningEnabled,
                setIsListeningEnabled
            }}
        >


            <VoiceDialog />
            {children}


            {/* <div className={`flex flex-col text-center w-full mb-24`}>
                <p className={`font-bold`}>Speech debug</p>
                <p>isListeningEnabled: {isListeningEnabled ? 'Yes' : 'No'}</p>
                <p>isListening: {isListening ? 'Yes' : 'No'}</p>
                <p>isRecognizerRunning: {isRecognizerRunning ? 'Yes' : 'No'}</p>
                <p>speechRecognitionSupported: {speechRecognitionSupported() ? 'Yes' : 'No'}</p>
                <p>Spoken answer: {spokenAnswer}</p>
                <p>Final spoken answer: {finalSpokenAnswer}</p>
                <p>Microphone permission: {microphonePermission ? 'Granted' : 'Denied'}</p>
                <p>Recognition status: {recognitionStatus}</p>
            </div> */}


        </VoiceContext.Provider>
    );
}
